// BETA 20.92 — Seeded RNG for BALANCE ROLLS (reproducible runs — the foundation
// for before/after balance A/B testing and seed-stable offers).
//
// `rollRng()` is a drop-in replacement for `Math.random()` at balance-relevant
// call sites: rarity rolls, perk/weapon magnitude jitter, shrine/chest/offer
// picks, drop rolls. With a fixed run seed, those draws reproduce the SAME
// sequence — so "same seed → same level-up options + magnitudes," which lets us
// change one number and feel the difference on the EXACT same offers.
//
// What we DELIBERATELY leave on Math.random: cosmetic randomness — particles,
// VFX jitter, audio variety, chat lines, screen-shake. Those should still vary
// every run, and seeding them would only make the eye-candy repeat.
//
// SAFETY: `rollRng()` ALWAYS returns a valid [0,1) number whether or not a run
// has seeded. Worst case (a roll before seeding) is non-reproducible, never
// broken. Production plays identically — this only adds reproducibility, it
// never changes the distribution of a roll.

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let _seed = 1;
let _next: () => number = mulberry32(_seed);

/** Optional override for the NEXT run's seed (set via window.__setRunSeed for
 *  reproducible tests). null = pick a fresh random seed each run. */
let _pendingOverride: number | null = null;

/** Seed the balance RNG for a run. Called once at run start. Returns the seed
 *  actually used (record it in telemetry so the run can be replayed by seed). */
export function seedRolls(seedHint?: number): number {
  const chosen = _pendingOverride != null
    ? _pendingOverride
    : (seedHint != null ? (seedHint | 0) : (Math.floor(Math.random() * 0x7fffffff) | 0));
  _seed = (chosen | 0) || 1;
  _next = mulberry32(_seed);
  _pendingOverride = null; // one-shot — a forced seed applies to one run only
  return _seed;
}

/** The run seed currently in effect. */
export function getRollSeed(): number {
  return _seed;
}

/** Force the NEXT run to use this seed (for reproducible balance testing). */
export function setPendingRunSeed(seed: number | null): void {
  _pendingOverride = seed == null ? null : (seed | 0);
}

/** Seeded [0,1) for BALANCE rolls. Drop-in for Math.random() at balance sites. */
export function rollRng(): number {
  return _next();
}
